import React, { useState, useMemo, useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './InstructionsToUse.module.css';
import { isHTMLString, isUploadedInstructionVideo, sanitizeHtml } from '../../../util/data';
import { IconCollection } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';

// Loaded only when a non-YouTube video actually renders — keeps it out of the
// main listing-page bundle for the majority of visitors.
//
// React.lazy can never be rendered by ReactDOMServer.renderToString (React
// error #294, "ReactDOMServer does not yet support Suspense"), so VideoItem
// only mounts this after hydration - see the `mounted` gate there. Reaching
// this element during SSR turns the whole listing page into a 500.
const ReactPlayer = React.lazy(() => import('react-player/file'));

// A video entry is only worth rendering when it has something playable. The
// legacy singular shape has been seen saved as `video: {}` (an empty object is
// truthy), which used to produce a phantom non-YouTube player - and, via the
// React.lazy above, a server-side 500 for every locale of that listing.
const hasPlayableVideo = item =>
  (typeof item?.videoUrl === 'string' && item.videoUrl.trim().length > 0) ||
  isUploadedInstructionVideo(item?.video);

const getYoutubeVideoId = url => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu.be')) return parsed.pathname.slice(1).split('?')[0];
    if (parsed.hostname.includes('youtube.com')) {
      if (parsed.pathname.includes('/embed/')) return parsed.pathname.split('/embed/')[1].split('?')[0];
      return parsed.searchParams.get('v');
    }
  } catch {
    return null;
  }
  return null;
};

const getVisibleText = str => {
  if (!str) return '';
  if (isHTMLString(str) && typeof window !== 'undefined') {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = str;
    return tempDiv.textContent || tempDiv.innerText || '';
  }
  return str;
};

const truncateHTML = (htmlString, maxLength = 1000) => {
  if (typeof window === 'undefined') return htmlString;
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  let currentLength = 0;
  let truncatedContent = '';
  let wasTruncated = false;
  const walkDOM = (node) => {
    if (!node || !node.nodeType) return;
    if (currentLength >= maxLength) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      const remainingLength = maxLength - currentLength;
      if (currentLength + text.length <= maxLength) {
        truncatedContent += text;
        currentLength += text.length;
      } else {
        truncatedContent += text.substring(0, remainingLength);
        currentLength = maxLength;
        wasTruncated = true;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      truncatedContent += `<${tagName}`;
      for (let i = 0; i < node.attributes.length; i++) {
        const attr = node.attributes[i];
        truncatedContent += ` ${attr.name}=\"${attr.value}\"`;
      }
      truncatedContent += '>';
      for (let i = 0; i < node.childNodes.length; i++) {
        if (currentLength >= maxLength) break;
        walkDOM(node.childNodes[i]);
      }
      if (currentLength < maxLength) {
        truncatedContent += `</${tagName}>`;
      }
    }
  };
  walkDOM(tempDiv);
  return wasTruncated ? truncatedContent + '...' : truncatedContent;
};

// ── Video metadata helpers ──────────────────────────────────────────
const formatVideoDuration = sec => {
  if (typeof sec !== 'number' || sec < 0) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatVideoCount = n => {
  if (typeof n !== 'number') return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return `${n}`;
};

const ClockIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="7.5" />
    <path d="M10 6v4l2.5 2.5" />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 10s3-5.5 8-5.5S18 10 18 10s-3 5.5-8 5.5S2 10 2 10Z" />
    <circle cx="10" cy="10" r="2" />
  </svg>
);

const LikeIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.2 8.2 10 4.5c.4-.8 1.4-1.1 2.2-.6.6.3.9 1 .8 1.7l-.3 2.6h3.1a1.8 1.8 0 0 1 1.8 2.1l-.8 4.6a2 2 0 0 1-2 1.7H8.2M8.2 8.2H5.6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.6m0-8v8" />
  </svg>
);
// ────────────────────────────────────────────────────────────────────

// RAS-2493: publicData.instructions.videos[] (new, backward-compatible array)
// takes precedence when present and non-empty. Otherwise falls back to the
// legacy singular video/videoUrl fields, wrapped as a single-item list, so
// existing listings that only ever set the singular fields keep rendering
// exactly as before — this fallback must not be removed.
const buildVideoList = content => {
  const videos = Array.isArray(content?.videos) ? content.videos.filter(hasPlayableVideo) : [];
  if (videos.length > 0) return videos;

  const { video, videoUrl, videoTitle, videoSummary, videoViewCount, videoLikeCount, videoDurationSec } = content || {};
  const legacy = { video, videoUrl, videoTitle, videoSummary, videoViewCount, videoLikeCount, videoDurationSec };
  return hasPlayableVideo(legacy) ? [legacy] : [];
};

const PlayerPlaceholder = ({ isMobile }) => (
  <div style={{ height: isMobile ? 188 : 414, background: '#000', borderRadius: 8 }} />
);

const VideoItem = ({ item, index }) => {
  const [playerError, setPlayerError] = useState(false);
  // The lazy ReactPlayer is a client-only widget: the server and the first
  // client render both emit the placeholder (identical markup, so hydration
  // matches), and the chunk is fetched right after mount.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const { video, videoUrl, videoTitle, videoSummary, videoViewCount, videoLikeCount, videoDurationSec } = item || {};
  const youtubeId = getYoutubeVideoId(videoUrl);
  // videoTitle is captured alongside the URL when the owner picks a video
  // (ListingUseInstructionsField.js) but was never rendered here - the only title a
  // visitor saw was the one YouTube paints inside its own player chrome, which truncates
  // as soon as the embed is narrow. Rendering it underneath, in the same left-aligned
  // meta column as the description, makes it readable at any width.
  const hasStats =
    videoDurationSec != null ||
    (videoViewCount != null && videoViewCount > 0) ||
    (videoLikeCount != null && videoLikeCount > 0);
  const hasMetadata = hasStats || !!videoSummary || !!videoTitle;

  return (
    <div className={css.videoItem}>
      <figure className={css.video}>
        {youtubeId ? (
          <div className={css.videoIframeWrapper}>
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}`}
              className={css.videoIframe}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              title={`Instructional video ${index + 1}`}
            />
          </div>
        ) : !playerError ? (
          mounted ? (
            <Suspense fallback={<PlayerPlaceholder isMobile={isMobile} />}>
              <ReactPlayer
                url={video?.playback_url || videoUrl}
                controls
                width="100%"
                height={isMobile ? 188 : 414}
                style={{ borderRadius: 8, background: '#000' }}
                onError={() => setPlayerError(true)}
              />
            </Suspense>
          ) : (
            <PlayerPlaceholder isMobile={isMobile} />
          )
        ) : (
          <a
            href={videoUrl || video?.playback_url}
            target="_blank"
            rel="noopener noreferrer"
            className={css.videoFallbackLink}
          >
            <FormattedMessage
              id="InstructionsToUse.watchOnYouTube"
              defaultMessage="Watch instructional video on YouTube ↗"
            />
          </a>
        )}
        <figcaption style={{ display: 'none' }}>
          <FormattedMessage id="InstructionsToUse.instructionalVideo" defaultMessage="Instructional video" />
        </figcaption>
      </figure>

      {hasMetadata && (
        <div className={css.videoMeta}>
          {videoTitle && <h4 className={css.videoTitle}>{videoTitle}</h4>}
          {hasStats && (
            <div className={css.videoMetaStats}>
              {videoDurationSec != null && (
                <span className={css.videoMetaStat}>
                  <ClockIcon />
                  {formatVideoDuration(videoDurationSec)}
                </span>
              )}
              {videoViewCount != null && videoViewCount > 0 && (
                <span className={css.videoMetaStat}>
                  <EyeIcon />
                  {formatVideoCount(videoViewCount)} views
                </span>
              )}
              {videoLikeCount != null && videoLikeCount > 0 && (
                <span className={css.videoMetaStat}>
                  <LikeIcon />
                  {formatVideoCount(videoLikeCount)} likes
                </span>
              )}
            </div>
          )}
          {videoSummary && (
            <p className={css.videoSummary}>{videoSummary}</p>
          )}
        </div>
      )}
    </div>
  );
};

const InstructionsToUse = ({ content }) => {
  const parsedContent = typeof content === 'object' && content !== null ? content : { text: content };
  const { text: instructionsText } = parsedContent;
  const text = sanitizeHtml(typeof instructionsText === 'string' ? instructionsText : '');
  const safeText = typeof text === 'string' ? text : '';

  const videoList = useMemo(() => buildVideoList(parsedContent), [
    parsedContent?.videos,
    parsedContent?.video,
    parsedContent?.videoUrl,
  ]);

  const [showFull, setShowFull] = useState(false);

  // Determine if mobile view
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Get the visible text (no HTML tags)
  const visibleText = useMemo(() => getVisibleText(safeText), [safeText]);

  // Truncate for display
  const truncatedText = useMemo(() => {
    const maxLength = isMobile ? 200 : 1000;

    if (isHTMLString(safeText)) {
      const truncated = truncateHTML(safeText, maxLength);
      // Check if the content was actually truncated (ends with ...)
      if (truncated.endsWith('...')) {
        return truncated;
      } else {
        // If not truncated by truncateHTML but still longer than maxLength, add ...
        const charCount = safeText.replace(/<[^>]*>/g, '').length;
        return charCount > maxLength ? truncated + '...' : truncated;
      }
    } else {
      return safeText.length > maxLength ? safeText.substring(0, maxLength) + '...' : safeText;
    }
  }, [safeText, isMobile]);

  // Only show the button if the visible text is longer than maxLength characters
  // and the truncated version is actually different from the full version
  const shouldTruncate = useMemo(() => {
    if (!safeText) return false;
    const maxLength = isMobile ? 200 : 1000;
    if (visibleText.length <= maxLength) return false;
    if (isHTMLString(safeText)) {
      return getVisibleText(truncatedText) !== visibleText;
    } else {
      return safeText.length > maxLength;
    }
  }, [safeText, visibleText, truncatedText, isMobile]);

  return (
    <section className={css.panel} aria-labelledby="instructions-heading">
      <h3 id="instructions-heading" className={css.heading}>
        <FormattedMessage id="InstructionsToUse.heading" defaultMessage="Tips & Instructions to Use" />
      </h3>

      <article className={css.content}>
        {safeText ? (
          isHTMLString(safeText) ? (
            <div dangerouslySetInnerHTML={{ __html: showFull ? safeText : truncatedText }} />
          ) : (
            <pre>{showFull ? safeText : truncatedText}</pre>
          )
        ) : videoList.length === 0 ? (
          <p>
            <FormattedMessage id="InstructionsToUse.noInstructions" defaultMessage="No instructions provided." />
          </p>
        ) : null}
        {shouldTruncate && (
          <button
            className={css.readMoreButton}
            onClick={() => setShowFull((v) => !v)}
            type="button"
          >
            {showFull ? (
              <FormattedMessage id="InstructionsToUse.readLess" defaultMessage="Read Less" />
            ) : (
              <FormattedMessage id="InstructionsToUse.readMore" defaultMessage="Read More" />
            )}
            <span
              className={css.enhanceCollapseIcon}
              style={{
                transform: showFull ? 'rotate(-180deg)' : 'rotate(0deg)',
              }}
              aria-hidden="true"

            >
              <IconCollection name={"read-more-arrow"} />
            </span>
          </button>
        )}
      </article>

      {videoList.length > 0 && (
        <div
          className={classNames(css.videoScroller, {
            // A lone video inherited the 2-up rail's 50% column, which on a phone left it
            // small enough that the player's own title bar truncated. .videoScrollerSingle
            // widens it to ~2/3 and centres it (mobile only - the desktop rail is already
            // wide enough at 50%).
            [css.videoScrollerSingle]: videoList.length === 1,
          })}
          data-testid="instructional-videos-scroller"
        >
          {videoList.map((item, idx) => (
            <VideoItem key={item?.videoUrl || item?.video?.id?.uuid || idx} item={item} index={idx} />
          ))}
        </div>
      )}
    </section>
  );
};

InstructionsToUse.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
};

export default InstructionsToUse;
